/**
 * Home Screen - MOBILE ONLY
 * 
 * Dashboard showing portfolio overview, attention items, and recent activity.
 * Uses real data from useHomeData hook per Rule 6.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, Pressable, RefreshControl, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore } from '../../store/authStore';
import { color, icon } from '../../shared/tokens/mobile';
import { Ionicons } from '@expo/vector-icons';
import { homeStyles as styles } from './styles/homeStyles';
import {
  ScreenContainer,
  SectionHeader,
  ListCard,
  ListRow,
  StatRow,
} from '../../components/v2/mobile';
import { useHomeData, formatRelativeTime } from '../../hooks/useHomeData';

// =============================================================================
// SCREEN
// =============================================================================

export default function HomeScreen() {
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const { stats, recentOpportunities, attentionItems, loading, refreshing, refresh, error } = useHomeData();

  const greeting = getGreeting();
  const userName = user?.display_name || user?.email?.split('@')[0] || 'User';

  const handleOpportunityPress = useCallback((opportunityId: number, portfolioId: number) => {
    router.push({
      pathname: '/v2/property/[id]',
      params: { id: opportunityId.toString(), portfolioId: portfolioId.toString() },
    } as any);
  }, [router]);

  const getAttentionIcon = (type: string): keyof typeof Ionicons.glyphMap => {
    switch (type) {
      case 'overdue': return 'alert-circle';
      case 'mention': return 'at';
      case 'status_change': return 'flag-outline';
      default: return 'notifications-outline';
    }
  };

  const getAttentionColor = (type: string): string => {
    switch (type) {
      case 'overdue': return color.error;
      case 'status_change': return color.warning;
      default: return color.textSecondary;
    }
  };

  // Loading state
  if (loading) {
    return (
      <ScreenContainer>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={color.primary} />
          <Text style={styles.loadingText}>Loading dashboard...</Text>
        </View>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl 
            refreshing={refreshing} 
            onRefresh={refresh} 
            tintColor={color.primary}
          />
        }
      >
        {/* HEADER */}
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text style={styles.greeting}>{greeting}</Text>
            <Text style={styles.userName}>{userName}</Text>
          </View>
          <Pressable
            style={styles.avatar}
            onPress={() => router.push('/v2/settings' as any)}
            accessibilityLabel="Account"
            accessibilityRole="button"
          >
            <Text style={styles.avatarInitial}>
              {userName.charAt(0).toUpperCase()}
            </Text>
          </Pressable>
        </View>

        {/* ERROR STATE */}
        {error && (
          <View style={styles.errorCard}>
            <Ionicons name="alert-circle" size={icon.md} color={color.error} />
            <Text style={styles.errorText}>{error}</Text>
            <Pressable onPress={refresh} style={styles.retryButton}>
              <Text style={styles.retryText}>Retry</Text>
            </Pressable>
          </View>
        )}

        {/* OVERVIEW */}
        <View style={styles.section}>
          <SectionHeader label="Overview" />
          <StatRow
            stats={[
              { value: String(stats.portfolioCount), label: 'Portfolios' },
              { value: String(stats.opportunityCount), label: 'Opportunities' },
              { value: String(stats.activeCount), label: 'Active' },
            ]}
          />
        </View>

        {/* NEEDS ATTENTION */}
        <View style={styles.section}>
          <SectionHeader
            label="Needs Attention"
            actionLabel={attentionItems.length > 0 ? 'View all' : undefined}
            onAction={() => router.push('/v2/activity' as any)}
          />
          {attentionItems.length > 0 ? (
            <ListCard>
              {attentionItems.map((item, index) => (
                <ListRow
                  key={item.id}
                  iconName={getAttentionIcon(item.type)}
                  iconColor={getAttentionColor(item.type)}
                  title={item.title}
                  subtitle={item.subtitle}
                  onPress={() => handleOpportunityPress(item.opportunityId, item.portfolioId)}
                  hideBorder={index === attentionItems.length - 1}
                />
              ))}
            </ListCard>
          ) : (
            <View style={styles.emptyCard}>
              <Ionicons name="checkmark-circle-outline" size={icon.lg} color={color.success} />
              <Text style={styles.emptyText}>All caught up!</Text>
            </View>
          )}
        </View>

        {/* RECENT OPPORTUNITIES */}
        <View style={styles.section}>
          <SectionHeader
            label="Recent Opportunities"
            actionLabel={recentOpportunities.length > 0 ? 'View all' : undefined}
            onAction={() => router.push('/v2/portfolio' as any)}
          />
          {recentOpportunities.length > 0 ? (
            <ListCard>
              {recentOpportunities.map((opp, index) => (
                <ListRow
                  key={opp.id}
                  iconName="location-outline"
                  title={opp.name}
                  subtitle={`Updated ${formatRelativeTime(opp.updatedAt)}`}
                  onPress={() => handleOpportunityPress(opp.id, opp.portfolioId)}
                  hideBorder={index === recentOpportunities.length - 1}
                />
              ))}
            </ListCard>
          ) : (
            <View style={styles.emptyCard}>
              <Ionicons name="add-circle-outline" size={icon.lg} color={color.textMuted} />
              <Text style={styles.emptyText}>No opportunities yet</Text>
              <Pressable 
                style={styles.addButton}
                onPress={() => router.push('/v2/map' as any)}
              >
                <Text style={styles.addButtonText}>Add from Map</Text>
              </Pressable>
            </View>
          )}
        </View>

        {/* COVERAGE */}
        <View style={styles.section}>
          <SectionHeader label="Coverage" />
          <ListCard>
            <ListRow
              iconName="map-outline"
              title="North Carolina"
              subtitle="7 counties available"
              onPress={() => router.push('/v2/admin/coverage' as any)}
              hideBorder
            />
          </ListCard>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning,';
  if (hour < 18) return 'Good afternoon,';
  return 'Good evening,';
}

// Styles extracted to ./styles/homeStyles.ts per cursor rules
